import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StatCard from '../../components/dashboard/StatCard';
import EmergencyAlertBanner from '../../components/dashboard/EmergencyAlertBanner';
import KhuPhoReportChart from '../../components/dashboard/KhuPhoReportChart';
import KhuPhoResolutionRateChart from '../../components/dashboard/KhuPhoResolutionRateChart';
import CategoryPieChart from '../../components/dashboard/CategoryPieChart';
import TopKhuPhoCard from '../../components/dashboard/TopKhuPhoCard';
import StatusHorizontalBarCard from '../../components/dashboard/StatusHorizontalBarCard';
import DeadlineDonutCard from '../../components/dashboard/DeadlineDonutCard';
import TrendLineChartCard from '../../components/dashboard/TrendLineChartCard';
import DepartmentPerformanceTable from '../../components/dashboard/DepartmentPerformanceTable';
import KhuPhoSelectModal from '../../components/dashboard/KhuPhoSelectModal';
import { AlertTriangle, CheckCircle, Clock, FileText, MessageSquare, Send, LogIn, Edit, FilePlus, Newspaper, History, BookOpen, Building2, Home, ClipboardList, CalendarCheck, Shield, Users, CheckCircle2, Megaphone, ArrowRight, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Check, Layers, Inbox, Search, ExternalLink } from 'lucide-react';
import { useReports } from '../../hooks/useReports';
import { useReportAreas } from '../../hooks/useReportAreas';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/vi';
import { showToast } from '../../utils/toastNotification';
import usePermission from '../../hooks/usePermission';
import VietnameseDatePicker from '../../components/documents/VietnameseDatePicker';
import { getEffectiveDeadline, getTimeRemaining } from '../../utils/badgeUtils';
import { ExtensionService } from '../../services/extensionService';

dayjs.extend(relativeTime);
dayjs.locale('vi');

const TIME_PRESETS = [
  { id: 'today', label: 'Hôm nay' },
  { id: 'yesterday', label: 'Hôm qua' },
  { id: '7days', label: '7 ngày qua' },
  { id: '30days', label: '30 ngày qua' },
  { id: 'thisMonth', label: 'Tháng này' },
  { id: 'all', label: 'Tất cả' },
  { id: 'custom', label: 'Khác' }
];

const getComparisonLabel = (preset) => {
  switch (preset) {
    case 'today':
      return 'so với hôm qua';
    case 'yesterday':
      return 'so với ngày trước đó';
    case '7days':
      return 'so với 7 ngày trước';
    case '30days':
      return 'so với 30 ngày trước';
    case 'thisMonth':
      return 'so với tháng trước';
    case 'all':
      return 'dữ liệu toàn thời gian';
    case 'custom':
      return 'so với giai đoạn trước';
    default:
      return 'so với kỳ trước';
  }
};

const getTimeFilterLabel = (preset, startDate, endDate) => {
  const matched = TIME_PRESETS.find((p) => p.id === preset);
  if (preset === 'custom' && startDate && endDate) {
    return `${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`;
  }
  return matched ? matched.label : 'Toàn thời gian';
};


const renderPercentBadge = (percentVal, direction, isLoading) => {
  if (isLoading || percentVal === undefined || percentVal === null) {
    return null;
  }
  const val = Number(percentVal) || 0;
  if (val === 0) {
    return null;
  }
  if (direction === 'down' || val < 0) {
    return (
      <span className="text-sm font-bold flex items-center gap-0.5 text-rose-600">
        ↓ {Math.abs(val)}%
      </span>
    );
  }
  return (
    <span className="text-sm font-bold flex items-center gap-0.5 text-emerald-600">
      ↑ {val}%
    </span>
  );
};




const LiveClock = React.memo(function LiveClock() {
  const [currentTime, setCurrentTime] = useState(() => dayjs().format('HH:mm:ss'));

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format('HH:mm:ss'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="h-10 bg-white rounded-lg border border-gray-200 shadow-xs px-3.5 flex items-center gap-2 text-xs md:text-sm font-semibold text-gray-700">
      <Clock className="w-4 h-4 text-blue-600 animate-pulse" />
      <span className="tabular-nums tracking-wide">{currentTime}</span>
    </div>
  );
});

export default function Dashboard() {
  const navigate = useNavigate();
  const { statistic, loadStatisticReport, reports, loadReports } = useReports();
  const { reportAreas, loadReportAreas } = useReportAreas({ autoFetch: false });

  const [selectedLinhVuc, setSelectedLinhVuc] = useState('all');
  const [isLinhVucOpen, setIsLinhVucOpen] = useState(false);
  const [linhVucSearchTerm, setLinhVucSearchTerm] = useState('');
  const linhVucDropdownRef = useRef(null);

  const [selectedPreset, setSelectedPreset] = useState('all');
  const [isPresetOpen, setIsPresetOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isKhuPhoModalOpen, setIsKhuPhoModalOpen] = useState(false);
  const [selectedKhuPho, setSelectedKhuPho] = useState('all');
  const presetDropdownRef = useRef(null);

  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    tong_so: null,
    tong_hom_nay: null,
    tong_nguoi_dan: null,
    qua_han: null,
    thong_ke_theo_trang_thai: null,
    nhat_ky_hoat_dong: []
  });

  const [error, setError] = useState(null);

  const activeUrgentReports = useMemo(() => {
    return (reports || []).filter(r => {
      const isUrgent = (r.muc_do || r.mucDo || '').toLowerCase().includes('khẩn');
      if (!isUrgent) return false;
      const status = r.lich_su_trang_thai?.[0]?.ten || r.trang_thai || r.trangThai || '';
      // Chỉ tính các đơn ở trạng thái "Đã gửi"
      const isSubmitted = ['Đã gửi', 'DA_GUI'].includes(status) || !status;
      if (!isSubmitted) return false;
      if (selectedLinhVuc && selectedLinhVuc !== 'all') {
        const reportAreaId = r.idLinhVucPhanAnh || r.id_linh_vuc || r.linh_vuc_phan_anh?.id;
        if (String(reportAreaId) !== String(selectedLinhVuc)) return false;
      }
      if (selectedKhuPho && selectedKhuPho !== 'all') {
        const kp = r.khu_pho || r.khuPho || '';
        const matchSelected = /(\d+)/.exec(selectedKhuPho);
        const matchKp = /(\d+)/.exec(kp);
        if (matchSelected && matchKp) {
          if (Number(matchSelected[1]) !== Number(matchKp[1])) return false;
        } else if (kp && selectedKhuPho && kp !== selectedKhuPho) {
          return false;
        }
      }
      return true;
    });
  }, [reports, selectedLinhVuc, selectedKhuPho]);

  const urgentCount = (dashboardData?.khan_cap !== undefined && dashboardData?.khan_cap !== null)
    ? dashboardData.khan_cap
    : activeUrgentReports.length;
  const { canAccessModule } = usePermission();

  const canViewReports = canAccessModule('PA');

  const [extensionPendingCount, setExtensionPendingCount] = useState(0);

  useEffect(() => {
    const unsubscribe = ExtensionService.subscribe((data) => {
      const pending = (data || []).filter((e) => e.status === "PENDING" || e.trang_thai === "PENDING").length;
      setExtensionPendingCount(pending);
    });
    ExtensionService.getStats()
      .then((data) => {
        if (data && typeof data.pending === 'number') {
          setExtensionPendingCount(data.pending);
        }
      })
      .catch(() => {});
    return () => unsubscribe();
  }, []);

  const activeOverdueCount = useMemo(() => {
    const now = new Date();
    return (reports || []).filter((r) => {
      const status = r.lich_su_trang_thai?.[0]?.ten || r.trang_thai || "";
      const isClosed = ["Đã giải quyết", "Đóng", "DA_GIAI_QUYET", "DONG", "Từ chối", "TU_CHOI"].includes(status);
      if (isClosed) return false;
      const deadline = getEffectiveDeadline(r);
      if (deadline) {
        return new Date(deadline) < now;
      }
      return (r.muc_do || "").toLowerCase().includes("khẩn");
    }).length;
  }, [reports]);

  useEffect(() => {
    loadReportAreas({ page: 1, size: 100, isActive: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (presetDropdownRef.current && !presetDropdownRef.current.contains(e.target)) {
        setIsPresetOpen(false);
      }
      if (linhVucDropdownRef.current && !linhVucDropdownRef.current.contains(e.target)) {
        setIsLinhVucOpen(false);
      }
    };
    if (isPresetOpen || isLinhVucOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPresetOpen, isLinhVucOpen]);

  const handleSelectPreset = (presetId) => {
    setSelectedPreset(presetId);
    setIsPresetOpen(false);
    const today = dayjs().format('YYYY-MM-DD');

    if (presetId === 'today') {
      setStartDate(today);
      setEndDate(today);
    } else if (presetId === 'yesterday') {
      const y = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      setStartDate(y);
      setEndDate(y);
    } else if (presetId === '7days') {
      setStartDate(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
      setEndDate(today);
    } else if (presetId === '30days') {
      setStartDate(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
      setEndDate(today);
    } else if (presetId === 'thisMonth') {
      setStartDate(dayjs().startOf('month').format('YYYY-MM-DD'));
      setEndDate(today);
    } else if (presetId === 'all') {
      setStartDate('');
      setEndDate('');
    } else if (presetId === 'custom') {
      setStartDate(startDate || today);
      setEndDate(endDate || today);
    }
  };

  useEffect(() => {
    setIsLoadingStats(true);
    const params = {};
    if (selectedPreset) {
      params.preset = selectedPreset;
    }
    if (selectedPreset === 'custom') {
      const today = dayjs().format('YYYY-MM-DD');
      params.startDate = startDate || today;
      params.endDate = endDate || today;
    } else if (selectedPreset !== 'all') {
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
    }
    if (selectedKhuPho && selectedKhuPho !== 'all') {
      params.khuPho = selectedKhuPho;
    }
    if (selectedLinhVuc && selectedLinhVuc !== 'all') {
      params.idLinhVuc = selectedLinhVuc;
      params.idLinhVucPhanAnh = selectedLinhVuc;
      params.linhVucId = selectedLinhVuc;
    }
    loadStatisticReport(params)
      .catch(error => {
        setError(error);
        showToast.error(error || 'Lấy dữ liệu thống kê thất bại!');
      })
      .finally(() => {
        setIsLoadingStats(false);
      });

    if (canViewReports) {
      const reportParams = { page: 1, size: 100 };
      if (selectedLinhVuc && selectedLinhVuc !== 'all') {
        reportParams.idLinhVucPhanAnh = selectedLinhVuc;
      }
      if (selectedKhuPho && selectedKhuPho !== 'all') {
        reportParams.khuPho = selectedKhuPho;
      }
      loadReports(reportParams).catch(error => {
        setError(error);
        showToast.error(error || 'Lấy danh sách phản ánh thất bại!');
      });
    }

    // Tải thống kê khi mount/đổi quyền/đổi bộ lọc thời gian/khu phố/lĩnh vực
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewReports, loadReports, loadStatisticReport, startDate, endDate, selectedPreset, selectedKhuPho, selectedLinhVuc]);

  useEffect(() => {
    if (statistic) {
      setDashboardData(statistic);
    }
  }, [statistic]);

  const getStatusColor = (status) => {
    const colors = {
      'Chờ xử lý': 'bg-orange-100 text-orange-800',
      'Đang xử lý': 'bg-blue-100 text-blue-800',
      'Đã giải quyết': 'bg-green-100 text-green-800',
      'Đã gửi': 'bg-gray-100 text-gray-800',
      'Đóng': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const [isExpandedUrgentList, setIsExpandedUrgentList] = useState(false);

  const overdueAndNearDueReports = useMemo(() => {
    const now = new Date();
    const activeReports = (reports || []).filter(r => {
      const status = r.lich_su_trang_thai?.[0]?.ten || r.trang_thai;
      const isStatusActive = status && !['Đóng', 'Đã giải quyết', 'Từ chối'].includes(status);
      if (!isStatusActive) return false;
      if (selectedLinhVuc && selectedLinhVuc !== 'all') {
        const reportAreaId = r.idLinhVucPhanAnh || r.id_linh_vuc || r.linh_vuc_phan_anh?.id;
        if (String(reportAreaId) !== String(selectedLinhVuc)) return false;
      }
      if (selectedKhuPho && selectedKhuPho !== 'all') {
        const kp = r.khu_pho || r.khuPho || '';
        const matchSelected = /(\d+)/.exec(selectedKhuPho);
        const matchKp = /(\d+)/.exec(kp);
        if (matchSelected && matchKp) {
          if (Number(matchSelected[1]) !== Number(matchKp[1])) return false;
        } else if (kp && selectedKhuPho && kp !== selectedKhuPho) {
          return false;
        }
      }
      return true;
    });

    const list = activeReports.map(report => {
      const deadline = getEffectiveDeadline(report);
      const isUrgent = (report?.muc_do || '').toLowerCase().includes('khẩn');
      let slaType = null;
      let deadlineText = null;

      if (deadline) {
        const dl = new Date(deadline);
        const diff = dl - now;
        if (diff < 0) {
          slaType = 'OVERDUE';
          deadlineText = getTimeRemaining(report);
        } else if (diff <= 24 * 60 * 60 * 1000) {
          slaType = 'NEAR_DUE';
          deadlineText = getTimeRemaining(report);
        }
      } else if (isUrgent) {
        slaType = 'NEAR_DUE';
        deadlineText = 'Khẩn cấp';
      }

      return {
        ...report,
        rawDeadline: deadline ? new Date(deadline).getTime() : Infinity,
        slaType,
        deadlineText,
        isUrgent
      };
    }).filter(item => item.slaType === 'OVERDUE' || item.slaType === 'NEAR_DUE');

    list.sort((a, b) => {
      if (a.slaType === 'OVERDUE' && b.slaType !== 'OVERDUE') return -1;
      if (a.slaType !== 'OVERDUE' && b.slaType === 'OVERDUE') return 1;
      return a.rawDeadline - b.rawDeadline;
    });

    return list;
  }, [reports, selectedLinhVuc, selectedKhuPho]);

  const visibleUrgentReports = useMemo(() => {
    if (isExpandedUrgentList) return overdueAndNearDueReports;
    return overdueAndNearDueReports.slice(0, 5);
  }, [isExpandedUrgentList, overdueAndNearDueReports]);

  const recentReports = overdueAndNearDueReports;


  const renderName = (data) => {
    switch (data) {
      case 'phan_anh':
        return 'Phản ánh';
      case 'lich_su_trang_thai':
        return 'Lịch sử trạng thái';
      default:
        return data;
    }
  }

  const renderIcon = (text) => {
    const lower = (text || "").toLowerCase();

    if (lower.includes("phan_anh") || lower.includes("phản ánh")) return MessageSquare;
    if (lower.includes("lich_su_trang_thai") || lower.includes("lịch sử trạng thái")) return History;
    if (lower.includes("người dùng")) return LogIn;
    if (lower.includes("chỉnh sửa")) return Edit;
    if (lower.includes("tạo mới")) return FilePlus;
    if (lower.includes("báo cáo")) return Newspaper;
    if (lower.includes("danh mục")) return BookOpen;
    if (lower.includes("mẫu đơn")) return FileText;
    if (lower.includes("uỷ ban")) return Building2;
    if (lower.includes("cơ sở dịch vụ")) return Home;
    if (lower.includes("đăng nhập")) return LogIn;
    if (lower.includes("thủ tục") || lower.includes("thủ tục hành chính")) return ClipboardList;
    if (lower.includes("tin tức") || lower.includes("tin tuc")) return Newspaper;
    if (lower.includes("lịch")) return CalendarCheck;
    if (lower.includes("phân quyền") || lower.includes("phan quyen") || lower.includes("phan_quyen") || lower.includes("vai trò") || lower.includes("vai tro")) return Shield;
    return FileText;
  };


  const iconColors = new Map([
    [MessageSquare, "#3b82f6"],
    [History, "#10b981"],
    [LogIn, "#ef4444"],
    [Edit, "#f97316"],
    [FilePlus, "#6366f1"],
    [Newspaper, "#0ea5e9"],
    [BookOpen, "#8b5cf6"],
    [FileText, "#6b7280"],
    [Building2, "#dc2626"],
    [Home, "#22c55e"],
    [ClipboardList, "#f59e0b"],
    [CalendarCheck, "#14b8a6"],
    [Shield, "#64748b"]
  ]);

  const getColorByIcon = (icon) => {
    return iconColors.get(icon) || "#6b7280";
  };

  const historyReport = (dashboardData.nhat_ky_hoat_dong || []).map(history => {
    return {
      id: history.timestamp,
      name: history.hanh_dong?.split(',').map((item) => renderName(item.trim())).join(', '),
      user: history?.nguoi_dung?.ho_va_ten,
      timeAgo: dayjs(history.timestamp).fromNow(),
      icon: renderIcon(history.hanh_dong),
      iconColor: getColorByIcon(renderIcon(history.hanh_dong))
    }
  });

  const khuPhoDataFormatted = useMemo(() => {
    const raw = dashboardData?.thong_ke_theo_khu_pho;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map((item) => ({
        name: item.name || item.khu_pho || "Khác",
        count: Number(item.count || item.total || 0),
      }));
    }
    if (raw && typeof raw === "object") {
      return Object.entries(raw).map(([kp, val]) => ({
        name: kp.startsWith("KP") ? kp : `KP ${kp}`,
        count: Number(val) || 0,
      }));
    }
    return undefined;
  }, [dashboardData?.thong_ke_theo_khu_pho]);

  const allAreaOptions = useMemo(() => {
    const list = [];
    if (Array.isArray(reportAreas) && reportAreas.length > 0) {
      reportAreas.forEach(a => {
        list.push({ id: a.id, ten: a.ten || a.ten_linh_vuc || a.name });
      });
    } else if (Array.isArray(dashboardData?.thong_ke_theo_linh_vuc)) {
      dashboardData.thong_ke_theo_linh_vuc.forEach(item => {
        if (item.id && (item.ten || item.name)) {
          list.push({ id: item.id, ten: item.ten || item.name });
        } else if (item.name) {
          list.push({ id: item.name, ten: item.name });
        }
      });
    }
    return list;
  }, [reportAreas, dashboardData?.thong_ke_theo_linh_vuc]);

  const filteredAreaOptions = useMemo(() => {
    if (!linhVucSearchTerm.trim()) return allAreaOptions;
    const term = linhVucSearchTerm.toLowerCase();
    return allAreaOptions.filter(a => a.ten?.toLowerCase().includes(term));
  }, [allAreaOptions, linhVucSearchTerm]);

  return (
    <div className="space-y-4 md:space-y-5 min-h-full">
      {/* Tiêu đề trang + Bộ chọn thời gian */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 m-0">Tổng quan</h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1 mb-0">Thống kê hoạt động hệ thống</p>
        </div>

        {/* Khối chọn thời gian */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Thời gian hiện tại (Tách component cách biệt để tránh re-render Dashboard) */}
          <LiveClock />

          {/* Nút / Dropdown: Lĩnh vực phản ánh */}
          <div className="relative" ref={linhVucDropdownRef}>
            <button
              type="button"
              onClick={() => {
                setIsLinhVucOpen(!isLinhVucOpen);
                setLinhVucSearchTerm('');
              }}
              className={`h-10 bg-white hover:bg-gray-50 border px-3.5 md:px-4 rounded-lg shadow-xs flex items-center gap-2 transition-all text-xs md:text-sm font-semibold cursor-pointer ${
                selectedLinhVuc && selectedLinhVuc !== 'all'
                  ? 'border-blue-500 text-blue-600 bg-blue-50/40'
                  : 'border-gray-200 text-gray-700'
              }`}
            >
              <Layers className="w-4 h-4 text-blue-600" />
              <span className="max-w-[120px] sm:max-w-[160px] truncate">
                {selectedLinhVuc === 'all'
                  ? 'Lĩnh vực phản ánh'
                  : (allAreaOptions.find(a => String(a.id) === String(selectedLinhVuc))?.ten || 'Lĩnh vực')}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isLinhVucOpen ? 'transform rotate-180' : ''}`} />
            </button>

            {isLinhVucOpen && (
              <div className="absolute left-0 mt-1.5 w-64 sm:w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-50 animate-in fade-in zoom-in-95 duration-150">
                {/* Search Input */}
                <div className="relative mb-1.5 px-0.5">
                  <input
                    type="text"
                    value={linhVucSearchTerm}
                    onChange={(e) => setLinhVucSearchTerm(e.target.value)}
                    placeholder="Tìm kiếm lĩnh vực..."
                    className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                </div>

                <div className="max-h-60 overflow-y-auto space-y-1 pr-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLinhVuc('all');
                      setIsLinhVucOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors flex items-center justify-between cursor-pointer ${
                      selectedLinhVuc === 'all'
                        ? 'bg-blue-50 text-blue-600 font-bold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>Tất cả lĩnh vực</span>
                    {selectedLinhVuc === 'all' && <Check className="w-4 h-4 text-blue-600" />}
                  </button>

                  {filteredAreaOptions.map((area) => {
                    const isSelected = String(selectedLinhVuc) === String(area.id);
                    return (
                      <button
                        key={area.id}
                        type="button"
                        onClick={() => {
                          setSelectedLinhVuc(area.id);
                          setIsLinhVucOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors flex items-center justify-between cursor-pointer ${
                          isSelected
                            ? 'bg-blue-50 text-blue-600 font-bold'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="truncate pr-2">{area.ten}</span>
                        {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                      </button>
                    );
                  })}

                  {filteredAreaOptions.length === 0 && (
                    <div className="text-center py-3 text-xs text-gray-400">
                      Không tìm thấy lĩnh vực
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Preset dropdown: Hôm nay, Hôm qua, 30 ngày qua... */}
          <div className="relative" ref={presetDropdownRef}>
            <button
              type="button"
              onClick={() => setIsPresetOpen(!isPresetOpen)}
              className="h-10 bg-white hover:bg-gray-50 border border-gray-200 px-4 rounded-lg shadow-xs flex items-center gap-2 transition-all text-xs md:text-sm font-semibold text-gray-700 cursor-pointer"
            >
              <span>{TIME_PRESETS.find(p => p.id === selectedPreset)?.label || 'Khác'}</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isPresetOpen ? 'transform rotate-180' : ''}`} />
            </button>

            {isPresetOpen && (
              <div className="absolute right-0 sm:left-0 mt-1.5 w-44 bg-white rounded-lg shadow-lg border border-gray-200 p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                {TIME_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleSelectPreset(preset.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-xs md:text-sm font-medium transition-colors flex items-center justify-between cursor-pointer ${
                      selectedPreset === preset.id
                        ? 'bg-blue-50 text-blue-600 font-bold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>{preset.label}</span>
                    {selectedPreset === preset.id && <Check className="w-4 h-4 text-blue-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date Range Container: Từ ngày — Đến ngày (bằng chiều cao h-10, borderless) */}
          <div className="h-10 bg-white rounded-lg border border-gray-200 shadow-xs px-3.5 flex items-center gap-2">
            <div className="w-28">
              <VietnameseDatePicker
                value={startDate}
                onChange={(iso) => {
                  const today = dayjs().format('YYYY-MM-DD');
                  setStartDate(iso);
                  if (!endDate) {
                    setEndDate(iso || today);
                  }
                  setSelectedPreset('custom');
                }}
                maxDate={endDate || undefined}
                placeholder="Từ ngày"
                borderless
              />
            </div>

            <span className="text-gray-300 font-semibold text-xs md:text-sm">—</span>

            <div className="w-28">
              <VietnameseDatePicker
                value={endDate}
                onChange={(iso) => {
                  const today = dayjs().format('YYYY-MM-DD');
                  setEndDate(iso);
                  if (!startDate) {
                    setStartDate(iso || today);
                  }
                  setSelectedPreset('custom');
                }}
                minDate={startDate || undefined}
                align="right"
                placeholder="Đến ngày"
                borderless
              />
            </div>
          </div>
        </div>
      </div>

      {/* 1. SECTION: KHẨN CẤP BANNER (Chỉ hiển thị khi đã tải xong và có phản ánh khẩn cấp ở trạng thái Đã gửi) */}
      {!isLoadingStats && canViewReports && urgentCount > 0 && (
        <EmergencyAlertBanner
          count={urgentCount}
          onScrollToUrgent={() => navigate('/reports?mucDo=Kh%E1%BA%A9n%20c%E1%BA%A5p&trangThai=%C4%90%C3%A3%20g%E1%BB%ADi')}
        />
      )}

      {/* 2. TOP 4 OVERVIEW KPI CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {/* Card 1: Khu phố (Nhấp để mở modal chọn khu phố) */}
        <div
          onClick={() => setIsKhuPhoModalOpen(true)}
          className="bg-white rounded-lg p-4 md:p-5 shadow-xs hover:shadow-sm hover:border-blue-300 border border-gray-200 flex items-center justify-between cursor-pointer transition-all group"
        >
          <div className="flex-1 min-w-0 pr-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm md:text-base font-semibold text-gray-600 block">Khu phố</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`font-bold text-gray-900 leading-tight truncate tracking-tight ${
                selectedKhuPho === 'all' ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl'
              }`}>
                {selectedKhuPho === 'all'
                  ? 'Tất cả khu phố'
                  : (selectedKhuPho.replace(/[^0-9]/g, '') || selectedKhuPho)}
              </span>
            </div>
            <span className="text-xs md:text-sm text-gray-400 mt-1 block font-normal group-hover:text-blue-600 transition-colors truncate">
              {selectedKhuPho === 'all'
                ? 'Toàn bộ địa bàn phường'
                : 'Nhấn để đổi khu phố'}
            </span>
          </div>
          <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white flex items-center justify-center flex-shrink-0 transition-all">
            <Building2 className="w-5 h-5" />
          </div>
        </div>

        {/* Card 2: Người dân sử dụng */}
        <div className="bg-white rounded-lg p-4 md:p-5 shadow-xs border border-gray-200 flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-3">
            <span className="text-sm md:text-base font-semibold text-gray-600 block mb-1">Người dân sử dụng</span>
            <div className="flex items-baseline gap-2.5">
              <span className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight tracking-tight">
                {isLoadingStats || dashboardData?.tong_nguoi_dan === null || dashboardData?.tong_nguoi_dan === undefined
                  ? '-'
                  : (dashboardData.tong_nguoi_dan >= 1000
                      ? `${(dashboardData.tong_nguoi_dan / 1000).toFixed(1)}K`
                      : dashboardData.tong_nguoi_dan)}
              </span>
              {renderPercentBadge(dashboardData?.pt_nguoi_dan, dashboardData?.huong_nguoi_dan, isLoadingStats)}
            </div>
            <span className="text-xs md:text-sm text-gray-400 mt-1 block font-normal">{getComparisonLabel(selectedPreset)}</span>
          </div>
          <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5" />
          </div>
        </div>

        {/* Card 3: Đơn phản ánh */}
        <div className="bg-white rounded-lg p-4 md:p-5 shadow-xs border border-gray-200 flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-3">
            <span className="text-sm md:text-base font-semibold text-gray-600 block mb-1">Đơn phản ánh</span>
            <div className="flex items-baseline gap-2.5">
              <span className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight tracking-tight">
                {isLoadingStats
                  ? '-'
                  : (dashboardData?.tong_tat_ca ?? dashboardData?.tong_so ?? (reports?.length || 0))}
              </span>
              {renderPercentBadge(dashboardData?.pt_phan_anh, dashboardData?.huong_phan_anh, isLoadingStats)}
            </div>
            <span className="text-xs md:text-sm text-gray-400 mt-1 block font-normal">{getComparisonLabel(selectedPreset)}</span>
          </div>
          <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5" />
          </div>
        </div>

        {/* Card 4: Tỷ lệ xử lý */}
        <div className="bg-white rounded-lg p-4 md:p-5 shadow-xs border border-gray-200 flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-3">
            <span className="text-sm md:text-base font-semibold text-gray-600 block mb-1">Tỷ lệ xử lý</span>
            <div className="flex items-baseline gap-2.5">
              <span className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight tracking-tight">
                {isLoadingStats || dashboardData?.ty_le_xu_ly === undefined || dashboardData?.ty_le_xu_ly === null
                  ? '-'
                  : `${dashboardData.ty_le_xu_ly}%`}
              </span>
              {renderPercentBadge(dashboardData?.pt_ty_le_xu_ly, dashboardData?.huong_ty_le_xu_ly, isLoadingStats)}
            </div>
            <span className="text-xs md:text-sm text-gray-400 mt-1 block font-normal">{getComparisonLabel(selectedPreset)}</span>
          </div>
          <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* 3. SECTION: TRẠNG THÁI PHẢN ÁNH */}
      <div className="bg-white rounded-lg p-4 md:p-5 shadow-xs border border-gray-200">
        {/* Header của khối */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <h2 className="text-xs md:text-sm font-bold text-gray-800 tracking-wider uppercase m-0">
            TRẠNG THÁI PHẢN ÁNH
          </h2>
        </div>

        {/* Hàng 5 chỉ số trạng thái kèm icon đặc trưng */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
          {/* 1. Phản ánh hôm nay */}
          <div 
            onClick={() => {
              const todayStr = dayjs().format('YYYY-MM-DD');
              navigate(`/reports?kyBaoCao=HOM_NAY&startDate=${todayStr}&endDate=${todayStr}`);
            }}
            className="p-3.5 md:p-4 rounded-lg bg-gray-50/70 border border-gray-100 flex flex-col justify-between transition-colors hover:bg-gray-100/80 cursor-pointer"
            title="Xem danh sách phản ánh hôm nay"
          >
            <div className="h-9 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Phản ánh hôm nay</span>
              <div className="w-9 h-9 rounded-lg bg-slate-200/60 text-slate-700 flex items-center justify-center flex-shrink-0">
                <Layers className="w-4.5 h-4.5" />
              </div>
            </div>
            <div className="mt-2 flex items-end justify-between">
              <span className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight tracking-tight">
                {isLoadingStats || dashboardData?.tong_hom_nay === null || dashboardData?.tong_hom_nay === undefined ? '-' : dashboardData.tong_hom_nay}
              </span>
            </div>
          </div>

          {/* 2. Mới tiếp nhận */}
          <div 
            onClick={() => navigate('/reports?trangThai=Đã gửi')}
            className="p-3.5 md:p-4 rounded-lg bg-indigo-50/40 border border-indigo-100/60 flex flex-col justify-between transition-colors hover:bg-indigo-50/80 cursor-pointer"
            title="Xem danh sách phản ánh mới tiếp nhận (Đã gửi)"
          >
            <div className="h-9 flex items-center justify-between">
              <span className="text-xs font-semibold text-indigo-900 uppercase tracking-wider">Mới tiếp nhận</span>
              <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
                <Inbox className="w-4.5 h-4.5" />
              </div>
            </div>
            <div className="mt-2 flex items-end justify-between">
              <span className="text-2xl md:text-3xl font-bold text-indigo-950 leading-tight tracking-tight">
                {isLoadingStats || !dashboardData?.thong_ke_theo_trang_thai ? '-' : (dashboardData.thong_ke_theo_trang_thai['Đã gửi'] ?? 0)}
              </span>
            </div>
          </div>

          {/* 3. Đang xử lý */}
          <div 
            onClick={() => navigate('/reports?trangThai=Đang xử lý')}
            className="p-3.5 md:p-4 rounded-lg bg-blue-50/40 border border-blue-100/60 flex flex-col justify-between transition-colors hover:bg-blue-50/80 cursor-pointer"
            title="Xem danh sách phản ánh đang xử lý"
          >
            <div className="h-9 flex items-center justify-between">
              <span className="text-xs font-semibold text-blue-900 uppercase tracking-wider">Đang xử lý</span>
              <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4.5 h-4.5" />
              </div>
            </div>
            <div className="mt-2 flex items-end justify-between">
              <span className="text-2xl md:text-3xl font-bold text-blue-950 leading-tight tracking-tight">
                {isLoadingStats || !dashboardData?.thong_ke_theo_trang_thai ? '-' : (dashboardData.thong_ke_theo_trang_thai['Đang xử lý'] ?? 0)}
              </span>
            </div>
          </div>

          {/* 4. Đã hoàn thành */}
          <div 
            onClick={() => navigate('/reports?trangThai=Đã giải quyết')}
            className="p-3.5 md:p-4 rounded-lg bg-emerald-50/40 border border-emerald-100/60 flex flex-col justify-between transition-colors hover:bg-emerald-50/80 cursor-pointer"
            title="Xem danh sách phản ánh đã giải quyết"
          >
            <div className="h-9 flex items-center justify-between">
              <span className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">Đã hoàn thành</span>
              <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4.5 h-4.5" />
              </div>
            </div>
            <div className="mt-2 flex items-end justify-between">
              <span className="text-2xl md:text-3xl font-bold text-emerald-950 leading-tight tracking-tight">
                {isLoadingStats || !dashboardData?.thong_ke_theo_trang_thai ? '-' : (dashboardData.thong_ke_theo_trang_thai['Đã giải quyết'] ?? 0)}
              </span>
            </div>
          </div>

          {/* 5. Quá hạn */}
          <div 
            onClick={() => navigate('/reports?tinhTrang=OVERDUE')}
            className="p-3.5 md:p-4 rounded-lg bg-rose-50/40 border border-rose-100/60 flex flex-col justify-between transition-all hover:bg-rose-50/80 cursor-pointer"
            title="Xem danh sách phản ánh quá hạn"
          >
            <div className="h-9 flex items-center justify-between">
              <span className="text-xs font-semibold text-rose-900 uppercase tracking-wider">Quá hạn</span>
              <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4.5 h-4.5" />
              </div>
            </div>

            <div className="mt-2 flex items-end justify-between gap-2">
              <span className="text-2xl md:text-3xl font-bold text-rose-950 leading-tight tracking-tight">
                {isLoadingStats || dashboardData?.qua_han === null || dashboardData?.qua_han === undefined ? '-' : dashboardData.qua_han}
              </span>
              <div className="flex flex-col items-start gap-0.5 text-[11px] text-rose-800">
                <span className="font-medium whitespace-nowrap" title="Đơn đang xử lý bị quá hạn">
                  <strong className="text-rose-950 font-bold">{dashboardData?.don_dang_tre ?? activeOverdueCount}</strong> đơn đang trễ
                </span>
                <span className="font-medium whitespace-nowrap" title="Đơn đang có đề nghị gia hạn chờ duyệt">
                  <strong className="text-amber-950 font-bold">{dashboardData?.don_cho_gia_han ?? extensionPendingCount}</strong> đơn chờ gia hạn
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 4. BIỂU ĐỒ 1: PHẢN ÁNH TIẾP NHẬN THEO KHU PHỐ + TOP KHU PHỐ XUẤT SẮC */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 xl:col-span-9">
          <KhuPhoReportChart
            data={khuPhoDataFormatted}
            isLoading={isLoadingStats}
            timeLabel={getTimeFilterLabel(selectedPreset, startDate, endDate)}
          />
        </div>
        <div className="lg:col-span-4 xl:col-span-3">
          <TopKhuPhoCard
            data={dashboardData?.top_khu_pho}
            isLoading={isLoadingStats}
            timeLabel={getTimeFilterLabel(selectedPreset, startDate, endDate)}
          />
        </div>
      </div>

      {/* 5. BIỂU ĐỒ 2: TỶ LỆ XỬ LÝ ĐÚNG HẠN THEO KHU PHỐ + PHÂN LOẠI PHẢN ÁNH */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 xl:col-span-9">
          <KhuPhoResolutionRateChart
            data={dashboardData?.ty_le_xu_ly_theo_khu_pho}
            isLoading={isLoadingStats}
            timeLabel={getTimeFilterLabel(selectedPreset, startDate, endDate)}
          />
        </div>
        <div className="lg:col-span-4 xl:col-span-3">
          <CategoryPieChart
            data={dashboardData?.thong_ke_theo_linh_vuc}
            isLoading={isLoadingStats}
            timeLabel={getTimeFilterLabel(selectedPreset, startDate, endDate)}
          />
        </div>
      </div>

      {/* 6. HÀNG 2 CARD: PHẢN ÁNH THEO TRẠNG THÁI + ĐÚNG HẠN VÀ TRỄ HẠN */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7">
          <StatusHorizontalBarCard
            data={[
              { label: 'Mới tiếp nhận', value: dashboardData?.thong_ke_theo_trang_thai?.['Đã gửi'] ?? 0, color: '#6366F1' },
              { label: 'Đang xử lý', value: dashboardData?.thong_ke_theo_trang_thai?.['Đang xử lý'] ?? 0, color: '#3B82F6' },
              { label: 'Đã hoàn thành', value: dashboardData?.thong_ke_theo_trang_thai?.['Đã giải quyết'] ?? 0, color: '#10B981' },
              { label: 'Đóng', value: dashboardData?.thong_ke_theo_trang_thai?.['Đóng'] ?? 0, color: '#EF4444' }
            ]}
            isLoading={isLoadingStats}
            timeLabel={getTimeFilterLabel(selectedPreset, startDate, endDate)}
          />
        </div>
        <div className="lg:col-span-5">
          <DeadlineDonutCard
            data={dashboardData?.thong_ke_theo_han_xu_ly}
            isLoading={isLoadingStats}
            timeLabel={getTimeFilterLabel(selectedPreset, startDate, endDate)}
          />
        </div>
      </div>

      {/* 7. CARD TOÀN CHIỀU RỘNG: XU HƯỚNG PHẢN ÁNH THEO THỜI GIAN */}
      <TrendLineChartCard
        data={dashboardData?.xu_huong_phan_anh}
        isLoading={isLoadingStats}
        timeLabel={getTimeFilterLabel(selectedPreset, startDate, endDate)}
      />

      {canViewReports && (
        <div className="bg-white rounded-xl p-4 md:p-5 shadow-xs border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm md:text-base font-bold text-gray-800 flex items-center gap-2 m-0">
              <span>Phản ánh quá hạn & sắp đến hạn</span>
              {overdueAndNearDueReports.length > 0 && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-md">
                  {overdueAndNearDueReports.length}
                </span>
              )}
            </h3>

            {overdueAndNearDueReports.length > 0 && (
              <button
                type="button"
                onClick={() => navigate('/reports?tinhTrang=OVERDUE')}
                className="text-xs md:text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <span>Xem trên trang quản lý</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {overdueAndNearDueReports.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-28 bg-gray-50/50 rounded-md border border-gray-100">
              <CheckCircle className="w-6 h-6 text-green-500 mb-1" />
              <div className="text-gray-500 font-medium text-xs md:text-sm">Không có phản ánh quá hạn hoặc sắp đến hạn</div>
            </div>
          ) : (
            <div className="space-y-2">
              {visibleUrgentReports.map((report) => (
                <div key={report?.id || report?.ma_phan_anh} className="pb-0.5">
                  <div
                    onClick={() => report?.id && navigate(`/reports/${report.id}`)}
                    className="border border-gray-200 hover:border-blue-400 hover:bg-blue-50/20 px-3.5 py-2.5 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-all cursor-pointer group"
                  >
                    <div className="flex-1 min-w-0 p-0.5">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span className="text-xs text-gray-500 font-mono font-medium">#{report?.ma_phan_anh}</span>
                        
                        {report?.slaType === 'OVERDUE' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-md border border-red-200">
                            <Clock className="w-3 h-3 animate-pulse" />
                            {report?.deadlineText || 'Quá hạn'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-semibold rounded-md border border-amber-200">
                            <Clock className="w-3 h-3" />
                            {report?.deadlineText || 'Sắp đến hạn'}
                          </span>
                        )}

                        {report?.isUrgent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-xs font-semibold rounded-md border border-red-100">
                            <AlertTriangle className="w-3 h-3" />
                            Khẩn
                          </span>
                        )}
                      </div>

                      <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-1 leading-tight line-clamp-2">
                        {report?.tieu_de}
                      </h4>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:text-sm text-gray-500">
                        <span className="inline-flex items-center py-0.5 text-gray-500 rounded-full text-xs md:text-sm">
                          {report?.linh_vuc_phan_anh?.ten || 'Chưa phân loại'}
                        </span>
                        <span className="hidden sm:inline text-gray-300">•</span>
                        {report?.ten_nguoi_phan_anh ? (
                          <span className="truncate max-w-full sm:max-w-xs">
                            {report?.ten_nguoi_phan_anh}
                            {report?.sdt_nguoi_phan_anh && <span className="hidden md:inline"> - {report?.sdt_nguoi_phan_anh}</span>}
                          </span>
                        ) : (
                          <span className="truncate max-w-full sm:max-w-xs">Ẩn danh</span>
                        )}
                        <span className="hidden sm:inline text-gray-300">•</span>
                        <span className="whitespace-nowrap">{dayjs(report?.thoi_gian_tao).fromNow()}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-center flex-shrink-0">
                      <span className={`inline-flex items-center px-3.5 py-1 text-xs md:text-sm font-semibold rounded-full ${getStatusColor(report?.lich_su_trang_thai?.[0]?.ten || report?.trang_thai)}`}>
                        {report?.lich_su_trang_thai?.[0]?.ten || report?.trang_thai || 'Chưa cập nhật'}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (report?.id) navigate(`/reports/${report.id}`);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white rounded-lg text-xs font-semibold border border-blue-200 hover:border-blue-600 transition-all cursor-pointer shadow-2xs"
                        title="Xem chi tiết đơn này"
                      >
                        <span>Chi tiết</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Nút Xem thêm / Thu gọn */}
              {overdueAndNearDueReports.length > 5 && (
                <div className="pt-2 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => setIsExpandedUrgentList((prev) => !prev)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 border border-gray-200 rounded-lg text-xs md:text-sm font-semibold transition-all cursor-pointer shadow-2xs"
                  >
                    {isExpandedUrgentList ? (
                      <>
                        <span>Thu gọn danh sách</span>
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      </>
                    ) : (
                      <>
                        <span>Xem thêm {overdueAndNearDueReports.length - 5} phản ánh quá hạn & sắp đến hạn khác</span>
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {/* HIỆU SUẤT ĐƠN VỊ XỬ LÝ */}
      <DepartmentPerformanceTable
        data={dashboardData?.hieu_suat_don_vi}
        isLoading={isLoadingStats}
      />

      {/* 8. NHẬT KÝ HOẠT ĐỘNG */}
      <div className="bg-white rounded-xl p-5 md:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-gray-100/70">
        <h3 className="text-base md:text-lg font-bold text-gray-800 mb-4">
          Nhật ký hoạt động
        </h3>

        {(() => {
          const logsToRender = historyReport && historyReport.length > 0 ? historyReport : [
            { id: 1, name: 'Người dân đã gửi phản ánh.', user: 'Lê Văn Cường', timeAgo: 'a day ago' },
            { id: 2, name: 'Phản ánh không được tiếp nhận do thiếu thông tin.', user: 'Lý Thị Oanh', timeAgo: 'a day ago' },
            { id: 3, name: 'Đơn vị phụ trách đang tiến hành xử lý.', user: 'Lý Thị Oanh', timeAgo: 'a day ago' },
            { id: 4, name: 'Phản ánh đã được tiếp nhận.', user: 'Đặng Minh Luân', timeAgo: 'a day ago' },
            { id: 5, name: 'Phản ánh đã được chuyển đến đơn vị xử lý.', user: 'Vũ Thị Hoa', timeAgo: 'a day ago' },
            { id: 6, name: 'Phản ánh đã được tiếp nhận.', user: 'Vũ Thị Hoa', timeAgo: 'a day ago' }
          ];

          return (
            <div className="space-y-4">
              {logsToRender.map((log, index) => (
                <div key={log.id || index} className="flex items-center gap-3.5 py-0.5">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs md:text-sm font-medium text-gray-800 leading-snug mb-0.5">
                      {log.name}
                    </p>
                    <p className="text-xs text-gray-400 font-normal m-0 flex items-center gap-1.5">
                      <span>{log.user ? log.user : 'Chưa cập nhật'}</span>
                      <span>•</span>
                      <span>{log.timeAgo}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* MODAL CHỌN KHU PHỐ (CHỈ ĐƯỢC CHỌN 1) */}
      <KhuPhoSelectModal
        isOpen={isKhuPhoModalOpen}
        onClose={() => setIsKhuPhoModalOpen(false)}
        selectedKhuPho={selectedKhuPho}
        onSelectKhuPho={(kp) => {
          setSelectedKhuPho(kp);
          showToast.success(kp === 'all' ? 'Đã chọn xem Tất cả khu phố' : `Đã chọn ${kp}`);
        }}
      />
    </div>
  );
}
